import { handleUnauthorized } from "../utils/auth/authHandler";

class ApiClient {
  static baseUrl = "";
  static csrfToken = null;

  static setBaseUrl(url) {
    if (!url) throw new Error("Base API URL is required");
    ApiClient.baseUrl = url;
  }

  static setCsrfToken(token) {
    ApiClient.csrfToken = token;
  }

  static initializeApiClient() {
    ApiClient.setCsrfToken(null);
  }

  static async getCsrfToken(allow401 = false) {
    if (ApiClient.csrfToken) return ApiClient.csrfToken;
    try {
      const response = await fetch(`${ApiClient.baseUrl}/auth/csrf-token`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          ApiClient.setCsrfToken(null);
          if (allow401) return null;
          handleUnauthorized();
          return null;
        }
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const csrfToken = response.headers.get("x-csrftoken");
      ApiClient.setCsrfToken(csrfToken);
      return ApiClient.csrfToken;
    } catch (error) {
      if (allow401 && (error.message?.includes("401") || error.status === 401))
        return null;
      throw error;
    }
  }

  /**
   * @private #request
   * @returns {Promise<Response>} - Returns the raw Fetch Response object
   */
  static async #request(
    method,
    endpoint,
    data = null,
    requiresCsrf = true,
    customHeaders = {},
  ) {
    const endpointUrl = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    const url = `${ApiClient.baseUrl}${endpointUrl}`;

    const headers = {
      "Content-Type": "application/json",
      ...customHeaders,
    };

    if (requiresCsrf) {
      const csrfToken = await ApiClient.getCsrfToken();
      if (csrfToken) headers["X-CSRFToken"] = csrfToken;
    }

    const options = {
      method,
      headers,
      credentials: "include",
    };

    // Ensure even for DELETE, if data exists, we stringify it.
    if (data !== null && data !== undefined) {
      if (data instanceof FormData) {
        delete headers["Content-Type"];
        options.body = data;
      } else {
        options.body = JSON.stringify(data);
      }
    }

    try {
      const response = await fetch(url, options);
      if (response.status === 401) {
        handleUnauthorized();
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  // PUBLIC METHODS
  static async get(endpoint, headers = {}) {
    return ApiClient.#request("GET", endpoint, null, false, headers);
  }
  static async post(endpoint, data = null, headers = {}) {
    return ApiClient.#request("POST", endpoint, data, true, headers);
  }

  // Do the same for put and patch just in case
  static async put(endpoint, data = null, headers = {}) {
    return ApiClient.#request("PUT", endpoint, data, true, headers);
  }

  static async patch(endpoint, data = null, headers = {}) {
    return ApiClient.#request("PATCH", endpoint, data, true, headers);
  }
  static async delete(endpoint, dataOrHeaders = null) {
    // If dataOrHeaders exists, we send it as the body (data).
    // This covers your Notification payload { model_type: ... }
    const body = dataOrHeaders;

    // We also pass it as customHeaders just in case it was a Filings/Docs header object.
    // #request is smart enough to filter out what it doesn't need.
    const headers = dataOrHeaders || {};

    return ApiClient.#request("DELETE", endpoint, body, true, headers);
  }
}

const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
ApiClient.setBaseUrl(apiBaseUrl);

export { ApiClient };
